import React, { memo, type CSSProperties, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { Box, xcss, type Space } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { AvatarWrapper } from '@atlassian/jira-issue-field-assignee/src/common/ui/read-view/popover/index.tsx';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import {
	useShowCompactMode,
	useCardFieldsSettings,
	useHasCardExtraFields,
} from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CARD_ROW_HEIGHT,
	COMPACT_CARD_ROW_HEIGHT,
	EXTRA_CARD_ROW_HEIGHT,
} from '../../../common/constants/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../feature-flags.tsx';
import type { CardId } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { makeGetCardGroupByParentId } from '../../../state/selectors/card-entries/index.tsx';
import {
	getCards,
	isCardSelected,
	makeGetCardGroupExpanded,
	backlogHasPriority,
	backlogHasEstimates,
	backlogHasFlaggedCards,
	getOrderedVersions,
} from '../../../state/selectors/cards/index.tsx';
import {
	useIsCMPBacklog,
	useIsCMPKanbanBacklog,
} from '../../../state/state-hooks/capabilities.tsx';
import { useShowCheckbox } from '../../../state/state-hooks/use-show-checkbox.tsx';
import { CardColor } from '../card/card-contents/color-field/index.tsx';
import { CardExtraField } from '../card/card-contents/extra-field/index.tsx';
import { FieldSeparator } from '../card/card-contents/extra-field/styled.tsx';
import { CARD_CONTAINER_SELECTOR } from '../card/constants.tsx';
import messages from '../card/messages.tsx';
import {
	AssigneeContainer,
	CardGroupKey,
	CheckboxContainer,
	ImageContainer,
	KeyContainer,
	OptionalFieldsContainer,
	SummaryAndParentContainer,
	CardContainer,
	ExtraFieldsContainer,
} from '../card/styled.tsx';
import { ScreenSize } from '../hooks/common/constants.tsx';
import { useContainerSize } from '../hooks/use-is-compact-view/index.tsx';
import { AssigneeLite } from './assignee-lite/index.tsx';
import { DueDate } from './due-date/index.tsx';
import { EpicLiteWrapper } from './epic-lite/epic-lite-wrapper/index.tsx';
import { EstimateLite } from './estimate-lite/index.tsx';
import { Flag } from './flag/index.tsx';
import { PriorityLite } from './priority-lite/index.tsx';
import { Status } from './status-lite/status-wrapper/index.tsx';
import { StoryPointLite } from './story-point-lite/index.tsx';
import { Versions } from './versions/index.tsx';

export type Props = {
	cardListId: string;
	// id is passed through from OwnProps
	id: CardId;
	style?: {
		[key: string]: string;
	};
	isAttachedToParent?: boolean;
};

const CardLiteExtended = ({ style = {}, id, cardListId, isAttachedToParent = false }: Props) => {
	const cards = useBacklogSelector(getCards);
	const card = cards[String(id)] || {};
	const { formatMessage } = useIntl();
	const {
		key: cardKey,
		summary: cardSummary,
		icon,
		isDone,
		isFlagged,
		extraFields,
		typeHierarchyLevel,
		parentKey,
	} = card;

	// TODO: Collect these at a higher level and pass them down
	const shouldRenderPriorityColumn = useBacklogSelector(backlogHasPriority);
	const shouldRenderEstimatesColumn = useBacklogSelector(backlogHasEstimates);
	const shouldRenderInlineAddFlag = useBacklogSelector(backlogHasFlaggedCards);
	const orderedVersions = useBacklogSelector(getOrderedVersions);

	const viewSettings = isViewSettingAsPanelExpEnabledWithNoExposure()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCardFieldsSettings()
		: {
				showIssueType: true,
				showIssueKey: true,
				showPriority: true,
				showAssignee: true,
				showEstimate: true,
				showDueDate: true,
				cardExtraFields: undefined,
			};

	const cardGroup = useBacklogSelector(makeGetCardGroupByParentId)(cardListId, id);
	const isCardGroupExpanded = useBacklogSelector(makeGetCardGroupExpanded)(id);
	const subtaskIds = cardGroup && cardGroup.cardIds;

	const hasLinkedSubtasks = subtaskIds !== undefined && subtaskIds.length > 0;
	const isSelected = useBacklogSelector((state) => isCardSelected(state)(id));
	const shouldShowChevron = hasLinkedSubtasks;

	const isSubtask = isSubtaskHierarchyLevel(typeHierarchyLevel);

	const hasExtraFields: boolean = extraFields ? extraFields?.length > 0 : false;

	const isCompanyManagedKanban = useIsCMPKanbanBacklog();
	const isCompanyManaged = useIsCMPBacklog();

	const { size: backlogContainerSize } = useContainerSize();

	const shouldShowCheckbox = useShowCheckbox();

	const showCompactMode = useShowCompactMode();
	const isCompactView = backlogContainerSize === ScreenSize.EXTRA_SMALL;

	const renderParentKey = () => {
		const parentKeyText =
			parentKey !== undefined
				? formatMessage(messages.parentKeyText, {
						parentKey,
					})
				: '';

		return parentKey !== undefined && isSubtask && !isAttachedToParent ? (
			<CardGroupKey>{`${parentKeyText}`}</CardGroupKey>
		) : null;
	};

	const renderCardColor = () => <CardColor id={card.id} />;

	const showCardExtraFieldsColumnBasedOnViewSettings =
		isViewSettingAsPanelExpEnabledWithNoExposure()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useHasCardExtraFields()
			: true;

	const renderExtraFields = () =>
		hasExtraFields &&
		extraFields &&
		showCardExtraFieldsColumnBasedOnViewSettings && (
			<ExtraFieldsContainer
				isSmartCardEnabled={expValEquals(
					'jsw_perf_format-links-in-custom-fields',
					'board-and-backlog',
					true,
				)}
				isAttachedToParent={isAttachedToParent && isCompanyManaged}
			>
				{extraFields
					.filter((field) => viewSettings.cardExtraFields?.[field.id] ?? true)
					.map((field, index) => (
						<React.Fragment key={field.id}>
							<CardExtraField field={field} />
							{index < extraFields?.length - 1 && (
								<FieldSeparator aria-hidden="true" key={`${field.id}-separator`}>
									•
								</FieldSeparator>
							)}
						</React.Fragment>
					))}
			</ExtraFieldsContainer>
		);

	const getCheckboxContainerMargin = (shouldAddMargin: boolean | undefined): Space => {
		if (fg('tanuki_a11y_fixes')) {
			if (shouldAddMargin && isAttachedToParent) {
				return 'space.500';
			}
			if (shouldAddMargin) {
				return 'space.300';
			}
		}
		if (shouldAddMargin && isAttachedToParent) {
			return 'space.400';
		}
		if (shouldAddMargin) {
			return 'space.250';
		}
		return 'space.0';
	};

	const getChevronIcon = () =>
		isCardGroupExpanded ? (
			<ChevronDownIcon
				label=""
				LEGACY_fallbackIcon={ChevronDownIconOld}
				color={token('color.icon')}
			/>
		) : (
			<ChevronRightIcon
				label=""
				LEGACY_fallbackIcon={ChevronRightIconOld}
				color={token('color.icon')}
			/>
		);

	return (
		<Container
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={style}
			hasExtraFields={hasExtraFields}
			isCompact={showCompactMode}
		>
			<CardColor id={id} />
			<CardContainer
				isCompanyManagedKanban={isCompanyManagedKanban}
				isCompact={showCompactMode}
				shouldShowChevron={shouldShowChevron}
				isFlagged={isFlagged}
				isDraggable={false}
				isDragHandle={false}
				hasExtraFields={hasExtraFields}
				lowerOpacity={false}
				isSelected={isSelected}
				hideTypeIcon={!viewSettings.showIssueType}
				isAssigneeShown={viewSettings.showAssignee}
				data-component-selector={CARD_CONTAINER_SELECTOR}
				isVisualRefreshBeta={isVisualRefreshEnabled()}
				isTanukiA11yFixes={fg('tanuki_a11y_fixes')}
			>
				<Box
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
					xcss={xcss({
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
						marginRight: getCheckboxContainerMargin(isCompanyManaged && !shouldShowChevron),
					})}
				>
					<CheckboxContainer shouldShowCheckbox={shouldShowCheckbox} />
				</Box>
				{renderCardColor()}

				{hasLinkedSubtasks && <IconWrapper>{getChevronIcon()}</IconWrapper>}
				{viewSettings.showIssueType && (
					<ImageContainer>
						<Icon src={icon} />
					</ImageContainer>
				)}
				{viewSettings.showIssueKey && (
					<KeyContainer>
						<Key done={isDone} cardKey={cardKey} />
					</KeyContainer>
				)}
				<SummaryAndParentContainer>
					<Summary cardSummary={cardSummary} />
				</SummaryAndParentContainer>

				{/* Render RHS */}
				{renderParentKey()}

				<OptionalFieldsContainer>
					{/* Ignore rest. */}
					<Versions
						card={card}
						isCompanyManaged={isCompanyManaged}
						backlogContainerSize={backlogContainerSize}
						isCardListContainsVersion={!isEmpty(orderedVersions)}
					/>
					<EpicLiteWrapper
						card={card}
						backlogContainerSize={backlogContainerSize}
						isCompactView={isCompactView}
						isSubtask={isSubtask}
					/>
					<Status cardKey={cardKey} backlogContainerSize={backlogContainerSize} />
					{/* renderParentKey */}
					{/* renderNumberOfChildren */}
					{viewSettings.showDueDate && <DueDate dueDate={card?.dueDate} isDone={isDone} />}
					<Flag isFlagged={isFlagged} shouldRenderInlineAddFlag={shouldRenderInlineAddFlag} />
					{viewSettings.showEstimate && (
						<>
							<EstimateLite shouldRenderEstimatesColumn={shouldRenderEstimatesColumn} />
							<StoryPointLite issue={card} />
						</>
					)}
					{viewSettings.showPriority && (
						<PriorityLite
							shouldRenderPriorityColumn={shouldRenderPriorityColumn}
							issueKey={cardKey}
						/>
					)}
				</OptionalFieldsContainer>

				<AssigneeContainer>
					<AvatarWrapper>
						<AssigneeLite issueKey={cardKey} />
					</AvatarWrapper>
				</AssigneeContainer>
				{renderExtraFields()}
			</CardContainer>
		</Container>
	);
};

const CardLiteExtendedMemo = memo(CardLiteExtended);

export default CardLiteExtendedMemo;

type SummaryProps = {
	cardSummary: string;
};

const Summary = ({ cardSummary }: SummaryProps) => (
	<div
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			color: `${token('color.text', colors.N800)}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			whiteSpace: 'nowrap',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			overflow: 'hidden',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			textOverflow: 'ellipsis',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			minWidth: 0,
		}}
	>
		{cardSummary}
	</div>
);

type KeyProps = {
	done: boolean;
	cardKey: string;
};

const Key = ({ cardKey, done }: KeyProps) => (
	<div
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			font: token('font.body.UNSAFE_small'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			color: token('color.text.subtle', '#44546F'),
			fontWeight: isVisualRefreshEnabled()
				? token('font.weight.regular')
				: token('font.weight.semibold'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			outline: 'none',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			marginTop: '0',
			textDecoration: `${done ? 'line-through' : 'none'}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			whiteSpace: 'nowrap',

			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			paddingRight: token('space.100', '0.5rem'),
		}}
	>
		{cardKey}
	</div>
);

type IconProps = {
	src: string;
};

const Icon = ({ src }: IconProps) => (
	<img
		alt=""
		// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		style={{ width: gridSize * 2, height: gridSize * 2, verticalAlign: 'text-bottom' }}
		src={src}
	/>
);

type ContainerProps = {
	children: ReactNode;
	style: CSSProperties;
	hasExtraFields: boolean;
	isCompact?: boolean;
};

/**
 * Container holds the card, this is where react-virtualized absolute position styles will be
 * applied.
 */
const Container = ({ children, style, hasExtraFields, isCompact }: ContainerProps) => {
	const cardRowHeight = isCompact ? COMPACT_CARD_ROW_HEIGHT : CARD_ROW_HEIGHT;
	return (
		<div
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				...style,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				backgroundColor: token('elevation.surface.raised', 'transparent'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				height: `${hasExtraFields ? cardRowHeight + EXTRA_CARD_ROW_HEIGHT : cardRowHeight}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				textDecoration: 'none',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				display: 'grid',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				alignItems: 'center',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				flexDirection: 'column',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				position: 'relative',
			}}
		>
			{children}
		</div>
	);
};

// Chevron icon wrapper, keep it consistent with src/packages/software/backlog/src/view/card-list/card/card-contents/view.tsx
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	gridColumn: 'chevron / span 1',
	gridRow: 'card-detail / span 1',
	display: 'flex',
	justifyContent: 'center',
});
